import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ApiError, SettingName } from 'src/api/generated';
import { useBooleanSetting } from 'hooks/settings/settings';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PiChartBarLight, PiPhone, PiStarLight } from 'react-icons/pi';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useCalls } from 'src/modules/shared/hooks/api/calls/calls';
import { CallFilters } from '../molecules/call-filters';
import { CallsTable } from '../organisms/calls-table';
import { useAllUsers } from 'src/modules/shared/hooks/api/admin/users';
import {
  type CallsSearch,
  callsSearchSchema,
} from '../../schemas/calls-search.schema';
import { Route as CallsRoute } from 'src/routes/calls.index';
import { Route as CallsReportsRoute } from 'src/routes/calls.reports';
import { Route as FeaturedCallsIndexRoute } from 'src/routes/featured-calls.index';
import { useCallContacts } from 'src/modules/shared/hooks/api/calls/call-contacts';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { SortDirection } from 'src/modules/shared/types/api/query-builder.type';
import { ReportPermission } from 'src/modules/shared/types/permissions.type';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { validateCallSearch } from '../../utils/call-search.util';
import type { CallSearchValidationError } from '../../types/call-search.type';
import { HTTP_STATUS_UNPROCESSABLE_ENTITY } from 'src/modules/shared/constants/api/api.constants';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_FIELD = 'startTime';

const ensureSortDirection = (direction?: string): SortDirection => {
  return direction === 'asc' ? 'asc' : 'desc';
};

const getSortField = (field?: string): string => {
  return field && field.trim().length > 0 ? field : DEFAULT_SORT_FIELD;
};

const normalizePageSize = (size?: number): number => {
  if (!size || size <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return size;
};

export const CallsPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setting: showReports } = useBooleanSetting(SettingName.SHOW_REPORTS);
  const { hasPermission: canReadReports } = useHasPermissions(
    ReportPermission.Read
  );
  const {
    search: searchTermFromRoute = '',
    agentId,
    pageSize: pageSizeFromRoute = DEFAULT_PAGE_SIZE,
    sortField,
    sortOrder,
  } = useSearch({
    from: CallsRoute.id,
    select: (search: CallsSearch) => ({
      search: search.search,
      agentId: search.agentId,
      pageSize: search.pageSize,
      sortField: search.sortField,
      sortOrder: search.sortOrder,
    }),
  });

  const [searchTerm, setSearchTerm] = useState(searchTermFromRoute);
  const searchValidation = useMemo(
    () => validateCallSearch(searchTerm),
    [searchTerm]
  );

  useEffect(() => {
    setSearchTerm(searchTermFromRoute);
  }, [searchTermFromRoute]);

  const sortDirection = ensureSortDirection(sortOrder);
  const sortFieldValue = getSortField(sortField);
  const pageSize = normalizePageSize(pageSizeFromRoute);

  const filters = useMemo(
    () => ({
      normalizedSearch: searchValidation.normalizedSearch,
      searchIsValid: searchValidation.isValid,
      agentId,
      pageSize,
      sortField: sortFieldValue,
      sortOrder: sortDirection,
    }),
    [
      agentId,
      pageSize,
      searchValidation.isValid,
      searchValidation.normalizedSearch,
      sortDirection,
      sortFieldValue,
    ]
  );

  const queryParams = useMemo(() => {
    const builder = createQuery()
      .orderBy(filters.sortField, filters.sortOrder)
      .take(filters.pageSize);

    if (filters.searchIsValid && filters.normalizedSearch) {
      builder.search(filters.normalizedSearch);
    }

    if (filters.agentId) {
      builder.equals('userId', filters.agentId);
    }

    return builder.build();
  }, [filters]);

  const { users, loading: usersLoading, error: usersError } = useAllUsers();
  const {
    calls,
    loading: callsLoading,
    error: callsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useCalls({
    ...queryParams,
    enabled: searchValidation.isValid,
  });

  const {
    contactsMap,
    loading: contactsLoading,
    error: contactsError,
  } = useCallContacts(calls, searchValidation.isValid);

  const handleNavigateSearch = (
    updater: (prev: CallsSearch) => CallsSearch
  ) => {
    void navigate({
      to: CallsRoute.to,
      search: (prev) => updater(callsSearchSchema.parse(prev)),
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    handleNavigateSearch((prev) => ({
      ...prev,
      search: value.trim() ? value : undefined,
    }));
  };

  const handleAgentChange = (nextAgentId?: number) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      agentId: nextAgentId,
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      pageSize: newSize,
    }));
  };

  const loading =
    (callsLoading || usersLoading || contactsLoading) && calls.length === 0;
  const error = callsError ?? usersError ?? contactsError;

  const getSearchValidationMessage = (
    validationError?: CallSearchValidationError
  ): string | undefined => {
    switch (validationError) {
      case 'too-short':
        return t('calls.search.validation.tooShort');
      case 'too-long':
        return t('calls.search.validation.tooLong');
      case 'too-many-words':
        return t('calls.search.validation.tooManyWords');
      case 'no-indexable-word':
        return t('calls.search.validation.noIndexableWord');
      case undefined:
        return undefined;
    }
  };

  const getCallsErrorDescription = (): string => {
    if (!(callsError instanceof ApiError)) {
      return t('calls.page.error.description');
    }

    if (callsError.status === HTTP_STATUS_UNPROCESSABLE_ENTITY) {
      return t('calls.page.error.searchTooBroad');
    }

    return t('calls.page.error.description');
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex flex-1 items-center justify-center p-6">
          <ErrorState
            title={t('calls.page.error.title')}
            description={getCallsErrorDescription()}
            onAction={() => refetch()}
            actionContent={t('calls.page.error.retry')}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="sticky top-0 z-20 border-b border-slate-100 bg-white px-4 py-1">
          <TokenPagination
            className="bg-white"
            pageSize={filters.pageSize}
            currentCount={calls.length}
            onPageSizeChange={handlePageSizeChange}
            onNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isFetchingNextPage}
          />
        </div>
        <div className="flex-1 overflow-auto px-4 pb-8 pt-4">
          <CallsTable
            calls={calls}
            contactsMap={contactsMap}
            users={users}
            loading={loading}
            isLoadingMore={isFetchingNextPage}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <Panel className="flex flex-col h-full">
        <PanelHeader
          className="px-4 py-3"
          actions={
            <div className="flex items-center gap-3">
              {showReports && canReadReports ? (
                <Button
                  variant="secondary"
                  appearance="outline"
                  size="md"
                  className="flex items-center gap-2"
                  onClick={() =>
                    navigate({
                      to: CallsReportsRoute.to,
                      search: { phoneVersion: 'v1', tab: 'basic' },
                    })
                  }
                >
                  <PiChartBarLight className="w-4 h-4" />
                  <span className="whitespace-nowrap">
                    {t('calls.page.actions.reports')}
                  </span>
                </Button>
              ) : null}
              <Button
                variant="secondary"
                appearance="outline"
                size="md"
                className="flex items-center gap-2"
                onClick={() =>
                  navigate({
                    to: FeaturedCallsIndexRoute.to,
                  })
                }
              >
                <PiStarLight className="w-4 h-4" />
                <span className="whitespace-nowrap">
                  {t('calls.page.actions.featured')}
                </span>
              </Button>
            </div>
          }
        >
          <div className="flex items-center gap-4">
            <PiPhone className="w-7 h-7 flex-shrink-0" />
            <Text as="h2" size="h2" weight="medium">
              {t('calls.page.title')}
            </Text>
          </div>
        </PanelHeader>

        <CallFilters
          search={searchTerm}
          searchError={getSearchValidationMessage(searchValidation.error)}
          agentId={filters.agentId}
          agents={users}
          agentsLoading={usersLoading}
          onSearchChange={handleSearchChange}
          onAgentChange={handleAgentChange}
        />

        {renderContent()}
      </Panel>
    </div>
  );
};
